import { useQuery } from "@tanstack/react-query";
import React from "react";
import { createSearchParams, Navigate, useLocation } from "react-router-dom";

import { useLocalStorage } from "@/common/hooks/use-local-storage";
import { useCompany } from "@/common/providers/company-provider";

import { usePostalCodesQuery } from "../../api/get-postal-codes";
import { usePostalCodeDialogParams } from "./postal-code-dialog";
import { usePostalCodeObject } from "./use-postal-code-object";

const Restoration = ({
  children,
  path,
}: {
  children: (params: string) => React.ReactNode;
  path: string;
}) => {
  const company = useCompany();
  const initialParams = createSearchParams({
    currency: company.country.currency,
    enforceDialog: "true",
  });

  const postalCodes = useQuery(usePostalCodesQuery());
  const { params } = usePostalCodeDialogParams();
  const [savedParams, setSavedParams] = useLocalStorage<string>(
    `${path}.${company.company?.id}`,
  );
  const savedURLParams = createSearchParams(savedParams);
  const postalCode = savedURLParams.get("postalCode");
  const postalCodeObject = usePostalCodeObject(postalCode ?? undefined);

  React.useEffect(() => {
    if (!params) {
      return;
    }

    setSavedParams(
      Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join("&"),
    );
  }, [params, setSavedParams]);

  if (postalCodes.status !== "success") {
    return null;
  }

  return (
    <>
      {children(
        postalCodeObject.valid && savedParams
          ? savedParams
          : initialParams.toString(),
      )}
    </>
  );
};

const Params = ({
  children,
  params,
}: React.PropsWithChildren<{ params: string }>) => {
  const searchParams = usePostalCodeDialogParams();
  const { pathname } = useLocation();

  if (!searchParams.params) {
    return <Navigate to={`${pathname}?${params}`} />;
  }

  return children;
};

export { Params, Restoration };
